import React from 'react';
import { Box, Typography } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const StatChartView = ({ dynamicChartData, realWorkingRate, renderTooltipContent, filterLabels }) => {
  const renderMultilineText = (text, x, startDy, maxLength = 4) => {
    if (!text) return null;
    if (text.length <= maxLength) {
      return <tspan x={x} dy={startDy}>{text}</tspan>;
    }
    
    const lines = [];
    for (let i = 0; i < text.length; i += maxLength) {
      lines.push(text.slice(i, i + maxLength));
    }
    
    return lines.map((line, index) => (
      <tspan key={index} x={x} dy={index === 0 ? startDy : 14}>
        {line}
      </tspan>
    ));
  };
  
  // ── 🔒 Recharts 자체 percent 대신 원본 payload(displayValue)를 사용하도록 변경 ──
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, payload }) => {
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-midAngle * RADIAN);
    const cos = Math.cos(-midAngle * RADIAN);

    const isNarrowSpace = percent < 0.05;
    const displayPercent = payload?.displayValue || `${(percent * 100).toFixed(1)}%`;

    if (isNarrowSpace) {
      const sx = cx + outerRadius * cos;
      const sy = cy + outerRadius * sin;
      
      const normalizedAngle = midAngle % 360;
      const isRightSide = cos >= 0; 
      const angleFactor = Math.abs(cos);

      let dynamicLength = 16;
      let horizontalLength = 12;
      let isTopArea = false;
      let isSideVertical = false; 

      if (normalizedAngle >= 25 && normalizedAngle <= 155) {
        isTopArea = true;
        dynamicLength = 34 - angleFactor * 8; 
        horizontalLength = 20;
      } 
      else if (angleFactor > 0.75) {
        isSideVertical = true;
        dynamicLength = 28; 
      }

      const mx = cx + (outerRadius + dynamicLength) * cos;
      const my = cy + (outerRadius + dynamicLength) * sin;
      
      let ex, ey, textX, textY, textAnchorMode;
      let percentDy = "13"; 

      if (isSideVertical) {
        const isPhysicallyTop = my < cy;
        const verticalOffset = isPhysicallyTop ? -18 : 18; 

        ex = mx; 
        ey = my + verticalOffset; 
        textX = ex; 
        textAnchorMode = "middle"; 

        if (isPhysicallyTop) {
          textY = ey - 22; 
          percentDy = "13"; 
        } else {
          textY = ey + 12; 
          percentDy = "13";
        }
      } else {
        ex = mx + (isRightSide ? horizontalLength : -horizontalLength);
        ey = my;
        textX = isRightSide ? ex + 6 : ex - 55;
        textY = ey;
        textAnchorMode = "start";
      }
      
      const finalTextY = isTopArea ? ey + 4 : textY;
      const firstLineDy = isTopArea ? -12 : (name.length > 5 ? -5 : 0);

      return (
        <g>
          <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="#9CA3AF" fill="none" strokeWidth={1} />
          <circle cx={ex} cy={ey} r={1.5} fill="#9CA3AF" />
          
          <text 
            x={textX} 
            y={finalTextY} 
            textAnchor={textAnchorMode} 
            dominantBaseline="central"
            style={{ fontSize: '10.5px', fontWeight: '600' }}
            fill="#4B5563"
          >
            {renderMultilineText(name, textX, firstLineDy, 5)}
            <tspan x={textX} dy={percentDy} style={{ fontWeight: '700', fill: '#6B7280' }}>
              {displayPercent}
            </tspan>
          </text>
        </g>
      );
    }

    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const insideX = cx + radius * cos;
    const insideY = cy + radius * sin;
    const isLongText = name && name.length > 4;

    return (
      <text 
        x={insideX} 
        y={insideY} 
        fill="#FFFFFF"
        textAnchor="middle" 
        dominantBaseline="central"
        style={{ fontSize: '13px', fontWeight: 'bold' }}
      >
        {renderMultilineText(name, insideX, isLongText ? "-14" : "-7", 4)}
        <tspan x={insideX} dy="16">{displayPercent}</tspan>
      </text>
    );
  };

  const getRateTitle = () => {
    if (filterLabels?.type === 'DPT_LIST') {
      return '전체 부서 실가동률';
    }
    const dptName = filterLabels?.dptInfo?.nm;
    if (dptName && dptName !== '전체 부서') {
      return `${dptName} 실가동률`;
    }
    return '실가동률';
  };

  return (
    <Box sx={{ width: '100%', height: 420, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      
      {/* ── 🎯 [수정 1] 가운데 가동률 텍스트 박스의 zIndex를 제거 또는 0으로 낮춤 ── */}
      <Box sx={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 0, pointerEvents: 'none', mt: -1 }}>
        <Typography variant="body2" sx={{ fontWeight: '700', color: '#4B5563', fontSize: '16px', letterSpacing: '-0.3px', mb: 0.5 }}>
          {getRateTitle()}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: '800', color: '#111827', fontSize: '36px' }}>
          {realWorkingRate}
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height="100%">
        {dynamicChartData.length > 0 ? (
          <PieChart>
            {/* ── 🎯 [수정 2] Tooltip 컴포넌트에 wrapperStyle로 최상단 레이어 권한(zIndex: 9999) 부여 ── */}
            <Tooltip 
              content={renderTooltipContent} 
              wrapperStyle={{ zIndex: 9999 }} 
            />
            <Pie 
              data={dynamicChartData} 
              cx="50%" 
              cy="50%" 
              innerRadius={95} 
              outerRadius={155} 
              paddingAngle={1.5} 
              dataKey="value" 
              label={renderCustomizedLabel} 
              labelLine={false} 
              startAngle={90} 
              endAngle={-270}
            >
              {dynamicChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} style={{ outline: 'none', cursor: 'pointer' }} />
              ))}
            </Pie>
          </PieChart>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="caption" color="text.secondary">통계 데이터가 없습니다.</Typography>
          </Box>
        )}
      </ResponsiveContainer>
    </Box>
  );
};

export default StatChartView;
